import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UnifiedDashboardData, LeadCohortMetrics, JobScheduleMetrics, ReconciliationData } from '@/types/reports';
import { startOfDayInTz, endOfDayInTz, DEFAULT_TIMEZONE } from '@/utils/timezone';
import { calculateConversionRate, calculateLossRate, calculateCompletionRate } from '@/utils/dateFieldStandards';
import { useVisibility } from '@/hooks/useVisibility';

export const useUnifiedDashboard = (startDate: Date, endDate: Date, timezone: string = DEFAULT_TIMEZONE) => {
  const { canSeeCompanyFinancials } = useVisibility();
  return useQuery({
    enabled: canSeeCompanyFinancials,
    queryKey: ['reports', 'unified-dashboard', startDate.toISOString(), endDate.toISOString(), timezone],
    queryFn: async (): Promise<UnifiedDashboardData> => {
      const start = startOfDayInTz(startDate, timezone).toISOString();
      const end = endOfDayInTz(endDate, timezone).toISOString();

      // ========== LEAD-BASED METRICS (filtered by created_at) ==========
      const { data: leadsInPeriod, error: leadsError } = await supabase
        .from('leads')
        .select('id, lead_number, status, job_id, created_at')
        .gte('created_at', start)
        .lte('created_at', end);

      if (leadsError) throw leadsError;

      const leads = leadsInPeriod || [];
      const totalLeads = leads.length;
      const openLeads = leads.filter(l => l.status === 'new').length;
      const followUpLeads = leads.filter(l => l.status === 'needs_follow_up').length;
      const convertedLeads = leads.filter(l => l.job_id !== null).length;
      const lostLeads = leads.filter(l => l.status === 'lost').length;

      // Use standardized rate calculations
      const conversionRate = calculateConversionRate(convertedLeads, totalLeads);
      const lossRate = calculateLossRate(lostLeads, totalLeads);

      const leadMetrics: LeadCohortMetrics = {
        totalLeads,
        openLeads,
        followUpLeads,
        convertedLeads,
        lostLeads,
        conversionRate,
        lossRate,
      };

      // ========== JOB-BASED METRICS (filtered by scheduled_date) ==========
      const { data: jobsInPeriod, error: jobsError } = await supabase
        .from('jobs')
        .select('id, job_number, status, lead_id, scheduled_date')
        .gte('scheduled_date', start)
        .lte('scheduled_date', end);

      if (jobsError) throw jobsError;

      const jobs = jobsInPeriod || [];
      const totalJobs = jobs.length;
      const scheduledJobs = jobs.filter(j => j.status === 'scheduled').length;
      const inProgressJobs = jobs.filter(j => j.status === 'in_progress').length;
      const onHoldJobs = jobs.filter(j => j.status === 'on_hold').length;
      const completedJobs = jobs.filter(j => j.status === 'completed').length;
      const cancelledJobs = jobs.filter(j => j.status === 'cancelled').length;

      // Use standardized rate calculations (both use total as denominator for consistency)
      const completionRate = calculateCompletionRate(completedJobs, totalJobs);
      const cancellationRate = totalJobs > 0 ? (cancelledJobs / totalJobs) * 100 : 0;

      const jobMetrics: JobScheduleMetrics = {
        totalJobs,
        scheduledJobs,
        inProgressJobs,
        onHoldJobs,
        completedJobs,
        cancelledJobs,
        completionRate,
        cancellationRate,
      };

      // ========== RECONCILIATION DATA ==========
      // 1. Leads converted in period with jobs scheduled OUTSIDE the period
      const convertedLeadIds = leads.filter(l => l.job_id !== null).map(l => l.job_id);

      let leadsWithJobsOutsidePeriodList: ReconciliationData['leadsWithJobsOutsidePeriodList'] = [];

      if (convertedLeadIds.length > 0) {
        const { data: jobsForConvertedLeads, error: jobsOutsideError } = await supabase
          .from('jobs')
          .select('id, job_number, scheduled_date, lead_number')
          .in('id', convertedLeadIds)
          .or(`scheduled_date.lt.${start},scheduled_date.gt.${end}`);

        if (jobsOutsideError) throw jobsOutsideError;

        if (jobsForConvertedLeads) {
          leadsWithJobsOutsidePeriodList = jobsForConvertedLeads.map(job => {
            const lead = leads.find(l => l.job_id === job.id);
            return {
              leadId: lead?.id || '',
              leadNumber: lead?.lead_number || job.lead_number,
              jobNumber: job.job_number,
              jobScheduledDate: job.scheduled_date || '',
            };
          });
        }
      }

      // 2. Jobs scheduled in period from leads created OUTSIDE the period
      const jobLeadIds = jobs.map(j => j.lead_id).filter(Boolean);

      let jobsFromLeadsOutsidePeriodList: ReconciliationData['jobsFromLeadsOutsidePeriodList'] = [];

      if (jobLeadIds.length > 0) {
        const { data: leadsForJobs, error: leadsOutsideError } = await supabase
          .from('leads')
          .select('id, lead_number, created_at')
          .in('id', jobLeadIds)
          .or(`created_at.lt.${start},created_at.gt.${end}`);

        if (leadsOutsideError) throw leadsOutsideError;

        if (leadsForJobs) {
          const leadMap = new Map(leadsForJobs.map(l => [l.id, l]));
          jobsFromLeadsOutsidePeriodList = jobs
            .filter(j => j.lead_id && leadMap.has(j.lead_id))
            .map(job => {
              const lead = leadMap.get(job.lead_id!);
              return {
                jobId: job.id,
                jobNumber: job.job_number,
                leadNumber: lead?.lead_number || '',
                leadCreatedDate: lead?.created_at || '',
              };
            });
        }
      }

      const reconciliation: ReconciliationData = {
        leadsWithJobsOutsidePeriod: leadsWithJobsOutsidePeriodList.length,
        jobsFromLeadsOutsidePeriod: jobsFromLeadsOutsidePeriodList.length,
        leadsWithJobsOutsidePeriodList,
        jobsFromLeadsOutsidePeriodList,
      };

      return {
        leadMetrics,
        jobMetrics,
        reconciliation,
      };
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
};
